import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  FileText,
  ArrowsClockwise,
  Copy,
  PencilSimple,
  Check,
  Envelope,
  Receipt,
  Spinner,
  Sparkle,
  Microphone,
  NotePencil,
  Lock,
  ArrowUp,
} from "phosphor-react";
import SmartToolsUpgradeCTA from "./SmartToolsUpgradeCTA";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * CustomerJobSummary - Generate and manage customer-facing job summaries
 * 
 * Props:
 * - jobId: Job ID
 * - onCopyToInvoiceNotes: Callback when copying to invoice notes
 * - onCopyToEmail: Callback when copying to email body
 * 
 * PARTIAL GATE:
 * - With smart_tools: Full AI generation, regenerate allowed
 * - Without smart_tools: Voice reuse + template only, regenerate disabled
 */
export function CustomerJobSummary({ 
  jobId, 
  onCopyToInvoiceNotes,
  onCopyToEmail 
}) {
  const [summary, setSummary] = useState("");
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState("");
  const [regenerationsRemaining, setRegenerationsRemaining] = useState(3);
  const [maxRegenerations, setMaxRegenerations] = useState(3);
  const [generatedAt, setGeneratedAt] = useState(null);
  
  // Feature gating state
  const [hasSmartTools, setHasSmartTools] = useState(true);
  const [upgradeCta, setUpgradeCta] = useState(null);
  const [trialRemaining, setTrialRemaining] = useState(null);
  const [trialLimit, setTrialLimit] = useState(null);

  useEffect(() => {
    fetchSummary();
    fetchAiCapabilities();
  }, [jobId]);

  const fetchAiCapabilities = async () => {
    try {
      const res = await fetch(`${API}/ai/capabilities`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setHasSmartTools(data.has_smart_tools ?? true);
        setUpgradeCta(data.upgrade_cta);
        // Set trial info for job summary AI
        setTrialRemaining(data.job_summary_ai?.trial_remaining ?? null);
        setTrialLimit(data.job_summary_ai?.trial_limit ?? null);
      }
    } catch (err) {
      console.error("Error fetching AI capabilities:", err);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await fetch(
        `${API}/jobs/${jobId}/customer-summary`,
        { credentials: "include" }
      );
      
      if (res.ok) {
        const data = await res.json();
        setSummary(data.summary || "");
        setSource(data.source || "");
        setGeneratedAt(data.generated_at);
        setRegenerationsRemaining(data.regenerations_remaining ?? 3);
        setMaxRegenerations(data.max_regenerations ?? 3);
        setEditedSummary(data.summary || "");
      }
    } catch (err) {
      console.error("Error fetching summary:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (regenerationsRemaining <= 0) {
      toast.error("No regenerations remaining today");
      return;
    }
    
    setRegenerating(true);
    try {
      const res = await fetch(
        `${API}/jobs/${jobId}/customer-summary/regenerate`,
        { 
          method: "POST",
          credentials: "include"
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        setSummary(data.summary);
        setSource(data.source);
        setGeneratedAt(data.generated_at);
        setRegenerationsRemaining(data.regenerations_remaining);
        setEditedSummary(data.summary);
        toast.success("Summary regenerated");
      } else if (res.status === 429) {
        toast.error("Rate limit reached. Try again tomorrow.");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to regenerate");
      }
    } catch (err) {
      toast.error("Failed to regenerate summary");
    } finally {
      setRegenerating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editedSummary.trim()) {
      toast.error("Summary cannot be empty");
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(
        `${API}/jobs/${jobId}/customer-summary`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ summary: editedSummary })
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        setSummary(data.summary);
        setSource(data.source);
        setIsEditing(false);
        toast.success("Summary saved");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch (err) {
      toast.error("Failed to save summary");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToInvoice = () => {
    if (onCopyToInvoiceNotes) {
      onCopyToInvoiceNotes(summary);
      toast.success("Summary copied to invoice notes");
    } else {
      navigator.clipboard.writeText(summary);
      toast.success("Summary copied to clipboard");
    }
  };

  const handleCopyToEmail = () => {
    if (onCopyToEmail) {
      onCopyToEmail(summary);
      toast.success("Summary ready for email");
    } else {
      navigator.clipboard.writeText(summary);
      toast.success("Summary copied to clipboard");
    }
  };

  const getSourceIcon = () => {
    switch (source) {
      case "voice":
        return <Microphone size={12} />;
      case "notes":
      case "ai":
        return <Sparkle size={12} />;
      case "edited":
        return <PencilSimple size={12} />;
      default:
        return <FileText size={12} />;
    }
  };

  const getSourceLabel = () => {
    switch (source) {
      case "voice":
        return "From voice note";
      case "notes":
        return "From tech notes";
      case "ai":
        return "AI generated";
      case "template":
        return "Template";
      case "edited":
        return "Edited";
      default:
        return "Generated";
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <Card className="border-2 border-zinc-200">
        <CardContent className="p-6 flex items-center justify-center">
          <Spinner size={20} className="animate-spin text-zinc-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-zinc-200" data-testid="customer-job-summary-card">
      <CardHeader className="border-b border-zinc-100 py-4">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <FileText size={20} />
            Customer Job Summary
          </CardTitle>
          <div className="flex items-center gap-2">
            {source && (
              <Badge 
                variant="secondary" 
                className="text-xs flex items-center gap-1 bg-zinc-100"
              >
                {getSourceIcon()}
                {getSourceLabel()}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {/* Summary Preview/Editor */}
        <div className="relative">
          {isEditing ? (
            <Textarea
              value={editedSummary}
              onChange={(e) => setEditedSummary(e.target.value)}
              className="min-h-[200px] resize-none border-2 focus:border-[#FF6600]"
              placeholder="Write a customer-friendly summary..."
              data-testid="summary-editor"
            />
          ) : (
            <div 
              className="p-4 bg-zinc-50 border border-zinc-200 rounded-sm min-h-[120px] whitespace-pre-wrap text-sm text-zinc-700"
              data-testid="summary-preview"
            >
              {summary || "No summary generated yet. Click 'Regenerate' to create one."}
            </div>
          )}
        </div>

        {/* Metadata */}
        {generatedAt && (
          <p className="text-xs text-zinc-400">
            Generated {formatDate(generatedAt)}
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          {isEditing ? (
            <>
              <Button
                onClick={handleSaveEdit}
                disabled={saving}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white gap-1"
                data-testid="save-summary-btn"
              >
                {saving ? (
                  <Spinner size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                Save
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setEditedSummary(summary);
                }}
                className="border-2"
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setIsEditing(true)}
                className="border-2 gap-1"
                data-testid="edit-summary-btn"
              >
                <PencilSimple size={14} />
                Edit
              </Button>
              
              {hasSmartTools ? (
                <Button
                  variant="outline"
                  onClick={handleRegenerate}
                  disabled={regenerating || regenerationsRemaining <= 0}
                  className="border-2 gap-1"
                  data-testid="regenerate-summary-btn"
                  title={`${regenerationsRemaining} regenerations remaining today`}
                >
                  <ArrowsClockwise size={14} className={regenerating ? "animate-spin" : ""} />
                  Regenerate ({regenerationsRemaining}/{maxRegenerations})
                </Button>
              ) : trialRemaining !== null && trialRemaining > 0 ? (
                // Trial mode - show trial remaining with upgrade option
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={handleRegenerate}
                    disabled={regenerating || regenerationsRemaining <= 0}
                    className="border-2 gap-1"
                    data-testid="regenerate-summary-btn"
                    title={`Free trial: ${trialRemaining} of ${trialLimit} remaining`}
                  >
                    <ArrowsClockwise size={14} className={regenerating ? "animate-spin" : ""} />
                    Regenerate
                  </Button>
                  <SmartToolsUpgradeCTA
                    location="job_summary"
                    trialRemaining={trialRemaining}
                    trialLimit={trialLimit}
                    compact={true}
                    showTrialInfo={true}
                  />
                </div>
              ) : (
                // Trial exhausted - show upgrade CTA
                <SmartToolsUpgradeCTA
                  location="job_summary"
                  trialRemaining={0}
                  trialLimit={trialLimit}
                  compact={true}
                />
              )}
            </>
          )}
        </div>

        {/* Copy Actions */}
        {!isEditing && summary && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-100">
            <Button
              variant="outline"
              onClick={handleCopyToInvoice}
              className="border-2 gap-1 flex-1"
              data-testid="copy-to-invoice-btn"
            >
              <Receipt size={14} />
              Copy to Invoice Notes
            </Button>
            
            <Button
              variant="outline"
              onClick={handleCopyToEmail}
              className="border-2 gap-1 flex-1"
              data-testid="copy-to-email-btn"
            >
              <Envelope size={14} />
              Copy to Email
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CustomerJobSummary;
